import moment from "moment";

/**
 * A calendar fetch window. Both values are instants at local day boundaries,
 * so serialise them with `toISOString()` - formatting them as `YYYY-MM-DD`
 * would discard the time and silently narrow the window by up to a day at
 * each edge.
 */
export type CalendarRange = { start: Date; end: Date };

/** Widen a window to cover whole local days at both ends. */
const toDayBounds = (start: Date, end: Date): CalendarRange => ({
  start: moment(start).startOf("day").toDate(),
  end: moment(end).endOf("day").toDate(),
});

/**
 * react-big-calendar hands onRangeChange an object for month and agenda views
 * but a bare array of dates for week and day. Normalise both to one shape so
 * the query key and the request never depend on which view is open.
 *
 * Both branches are widened to whole days because a view can report a
 * narrower window than it actually draws, and anything the view draws has to
 * be fetched or it silently goes missing. Agenda reports a bare 30-day span
 * from the current instant while rendering from the start of that first day
 * to the end of the last; the week and day arrays carry only the start of
 * each visible day, so their final day would be dropped entirely and a
 * single-day view would send an inverted, empty window.
 */
export const normaliseCalendarRange = (
  range: Date[] | CalendarRange
): CalendarRange => {
  if (Array.isArray(range)) {
    return toDayBounds(range[0], range[range.length - 1]);
  }

  return toDayBounds(range.start, range.end);
};

/**
 * onRangeChange does not fire on mount, so the first window is computed here.
 * The month view renders whole weeks, so the grid reaches back into the
 * previous month and forward into the next.
 */
export const initialCalendarRange = (date: Date): CalendarRange => ({
  start: moment(date).startOf("month").startOf("week").toDate(),
  end: moment(date).endOf("month").endOf("week").toDate(),
});

/**
 * The window a view covers at `date`, for the times the page changes the view
 * itself rather than the toolbar doing it.
 *
 * react-big-calendar only fires `onRangeChange` from its own navigate and
 * view-change handlers (see `Calendar.js`: `handleNavigate` and
 * `handleViewChange`), so moving the `view` prop from outside leaves the
 * fetch window where the old view left it. Switching from a week back to a
 * month would then draw a month grid holding only that week's jobs.
 *
 * Each branch is widened to whole local days by the same helper the
 * `onRangeChange` path uses, so a window computed here and one reported by
 * the calendar for the same view agree.
 */
export const rangeForView = (view: string, date: Date): CalendarRange => {
  switch (view) {
    case "day":
      return toDayBounds(date, date);
    case "week":
    case "work_week":
      // work_week is covered by the full week: a wider window fetches a few
      // jobs the view will not draw, where a narrower one would hide jobs it
      // does draw.
      return toDayBounds(
        moment(date).startOf("week").toDate(),
        moment(date).endOf("week").toDate()
      );
    case "agenda":
      // Agenda's default `length` is 30 days from the current date.
      return toDayBounds(date, moment(date).add(30, "days").toDate());
    default:
      return initialCalendarRange(date);
  }
};
